import React, { createContext, useContext, useMemo, useReducer } from 'react'
import type { PalworldCartContextType, PalworldCartLine, PalworldCartState } from '../types/palworldCart'
import type { PalworldItem } from '../types/palworldItems'

const initialState: PalworldCartState = {
  items: [],
  isOpen: false,
  total: 0,
  totalItems: 0
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { item: PalworldItem; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'SET_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'SET_CART_OPEN'; payload: boolean }

const computeTotals = (items: PalworldCartLine[]) => {
  const total = items.reduce((sum, line) => sum + (Number(line.item.price ?? 0) || 0) * line.quantity, 0)
  const totalItems = items.reduce((sum, line) => sum + line.quantity, 0)
  return { total, totalItems }
}

const clampQty = (q: number) => Math.max(1, Math.trunc(Number(q) || 1))

const palworldCartReducer = (state: PalworldCartState, action: CartAction): PalworldCartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { item, quantity } = action.payload
      const qty = clampQty(quantity)
      const idx = state.items.findIndex((l) => l.id === item.id)
      const items =
        idx >= 0
          ? state.items.map((l) => (l.id === item.id ? { ...l, quantity: l.quantity + qty } : l))
          : [...state.items, { id: item.id, item, quantity: qty, addedAt: new Date() }]
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems }
    }
    case 'REMOVE_ITEM': {
      const items = state.items.filter((l) => l.id !== action.payload)
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems }
    }
    case 'SET_QUANTITY': {
      const qty = clampQty(action.payload.quantity)
      const items = state.items.map((l) => (l.id === action.payload.id ? { ...l, quantity: qty } : l))
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems }
    }
    case 'CLEAR_CART':
      return initialState
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen }
    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload }
    default:
      return state
  }
}

const PalworldCartContext = createContext<PalworldCartContextType | undefined>(undefined)

export const usePalworldCart = () => {
  const ctx = useContext(PalworldCartContext)
  if (!ctx) throw new Error('usePalworldCart deve ser usado dentro de PalworldCartProvider')
  return ctx
}

export const PalworldCartProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(palworldCartReducer, initialState)

  const addItem = (item: PalworldItem, quantity: number) => dispatch({ type: 'ADD_ITEM', payload: { item, quantity } })
  const removeItem = (itemId: string) => dispatch({ type: 'REMOVE_ITEM', payload: itemId })
  const setQuantity = (itemId: string, quantity: number) =>
    dispatch({ type: 'SET_QUANTITY', payload: { id: itemId, quantity } })
  const clearCart = () => dispatch({ type: 'CLEAR_CART' })
  const toggleCart = () => dispatch({ type: 'TOGGLE_CART' })
  const setCartOpen = (open: boolean) => dispatch({ type: 'SET_CART_OPEN', payload: open })

  const getItemQuantity = (itemId: string): number => state.items.find((l) => l.id === itemId)?.quantity ?? 0

  const value = useMemo<PalworldCartContextType>(
    () => ({
      state,
      addItem,
      removeItem,
      setQuantity,
      clearCart,
      toggleCart,
      setCartOpen,
      getItemQuantity
    }),
    [state]
  )

  return <PalworldCartContext.Provider value={value}>{children}</PalworldCartContext.Provider>
}

